import { Option } from 'util/multi-select';
import SelectBase from '../select-field/select-base/select-base.component';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AutocompleteBase, {
  AutocompleteBaseProps,
} from '../autocomplete-field/autocomplete-base/autocomplete-base.component';
import { SearchOutlined } from '@mui/icons-material';
import IncFormHelperText from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type CategoryOption = Option & {
  category: string;
};

export type CategoryAutocompleteBaseProps<OptionType extends CategoryOption> = {
  categories: Option[];
  category: string;
  setCategory: (newCategory: string) => void;
  options: OptionType[];
  inputValue: string;
  setInputValue: (newValue: string) => void;
  value: OptionType[];
  onChange: (e, newValue: OptionType[]) => void;
  loading: boolean;
  error: any;
  helperText?: string;
  label?: string;
  allSelected?: boolean;
  selectAll?: boolean;
  autoFocus?: boolean;
  disableCategorySelect?: boolean;
} & AutocompleteBaseProps;

const selectWidth = '100px';

export const CategoryAutocompleteBase = React.forwardRef(
  <OptionType extends CategoryOption>(
    {
      categories,
      category,
      setCategory,
      options,
      inputValue,
      setInputValue,
      value,
      onChange,
      loading,
      error,
      helperText,
      label,
      allSelected,
      selectAll,
      autoFocus,
      renderOption,
      disableCategorySelect = false,
      limitTags = 0,
      ...rest
    }: CategoryAutocompleteBaseProps<OptionType>,
    ref,
  ) => {
    const { t } = useTranslation();

    const defaultRenderOption = selectAll
      ? (props, option, { selected }) => {
          const selectAllProps =
            option.id === 'ALL' ? { checked: allSelected } : {};
          return (
            <li {...props} data-testid="Autocomplete.Option" key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                sx={{ mr: 0 }}
                checked={selected}
                {...selectAllProps}
              />
              {option.label}
            </li>
          );
        }
      : undefined;

    const finalRenderOption = renderOption ?? defaultRenderOption;

    return (
      <FormControl error={!!error} sx={{ mt: 2, mb: 1 }}>
        <Box>
          <Box sx={{ display: 'inline-block' }}>
            <SelectBase
              options={categories}
              value={category}
              onChange={event => {
                setCategory(event.target.value);
              }}
              fullWidth={false}
              sx={{
                '& fieldset': {
                  borderRadius: { xs: '4px 0 0 4px' },
                },
                m: 0,
                width: selectWidth,
              }}
              error={!!error}
              data-testid="CategoryAutocompleteBase.Select"
              disabled={disableCategorySelect}
            />
          </Box>
          <Box
            sx={{
              display: 'inline-block',
              width: `calc(100% - ${selectWidth})`,
            }}
          >
            <AutocompleteBase
              options={options ?? []}
              value={value}
              onChange={onChange}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              multiple={true}
              limitTags={limitTags}
              loading={loading}
              inputValue={inputValue}
              onInputChange={(_e, newValue, reason) => {
                if (!['reset', 'selectOption', 'createOption'].includes(reason))
                  setInputValue(newValue);
              }}
              sx={{
                flex: 1,
                '& fieldset': {
                  borderRadius: { xs: '0 4px 4px 0' },
                },
              }}
              fullWidth={false}
              renderInput={params => (
                <TextField
                  {...params}
                  label={label}
                  error={!!error}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchOutlined />
                        </InputAdornment>
                        {limitTags > 0
                          ? params.InputProps.startAdornment
                          : null}
                      </>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {loading && <CircularProgress size={20} />}
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off', // disable autocomplete and autofill
                    'data-testid':
                      'CategoryAutocompleteBase.Autocomplete.Input',
                  }}
                  inputRef={ref}
                  sx={{ m: 0 }}
                  autoFocus={autoFocus}
                />
              )}
              data-testid="CategoryAutocompleteBase.Autocomplete"
              filterOptions={options => {
                if (options.length > 0) {
                  return selectAll
                    ? [
                        {
                          label: t(
                            'forms.fields.cardListItem.options.selectAll',
                            {
                              options: options.length,
                            },
                          ),
                          id: 'ALL',
                        },
                        ...options,
                      ]
                    : options;
                } else return options;
              }}
              {...(finalRenderOption
                ? { renderOption: finalRenderOption }
                : {})}
              disableClearable
              freeSolo
              {...rest}
            />
          </Box>
        </Box>
        <FormHelperText>
          <IncFormHelperText
            error={error}
            helperText={helperText}
            data-testid="CategoryAutocompleteBase.IncFormHelperText"
          />
        </FormHelperText>
      </FormControl>
    );
  },
);

export default CategoryAutocompleteBase;
